<script setup lang="ts">
/**
 * Masked SSN with reveal-on-demand.
 *
 * Shows `••• •• 1234` until the eye is clicked, then fetches the full SSN
 * from a reveal endpoint — which writes an audit_logs row per reveal
 * (fail-closed server-side). The full value is never in the list payload;
 * it lives only in this component's local state and resets on unmount, so
 * re-opening a card is a fresh, freshly-audited reveal.
 *
 * Two sources, because the id differs: `voe` (default) keys off the VOE
 * record id, `employee` keys off the driver's employee id for the ops
 * Drivers roster. Both endpoints audit identically.
 *
 * Typography comes from the parent — put font classes on the element that
 * wraps this component.
 */
import { ref } from 'vue'
import { voeService } from '../services/voe.service'
import { employeeService } from '../services/employee.service'

interface Props {
  /** VOE record id when source is 'voe', employee id when source is 'employee'. */
  recordId: string
  last4: string | null
  source?: 'voe' | 'employee'
}

const props = withDefaults(defineProps<Props>(), { source: 'voe' })

const fullSsn = ref<string | null>(null)
const shown = ref(false)
const loading = ref(false)
const error = ref(false)

async function toggle() {
  if (shown.value) {
    shown.value = false
    return
  }
  if (!fullSsn.value) {
    loading.value = true
    error.value = false
    try {
      const data =
        props.source === 'employee'
          ? await employeeService.revealSsn(props.recordId)
          : await voeService.revealDriverSsn(props.recordId)
      fullSsn.value = data.ssn
    } catch {
      error.value = true
      return
    } finally {
      loading.value = false
    }
  }
  shown.value = true
}
</script>

<template>
  <span class="inline-flex items-center gap-1.5">
    <span>{{ shown && fullSsn ? fullSsn : last4 ? `••• •• ${last4}` : '—' }}</span>
    <button
      v-if="last4"
      type="button"
      class="text-surface-400 hover:text-surface-700 transition-colors leading-none"
      :title="shown ? 'Hide SSN' : 'Show full SSN'"
      :disabled="loading"
      @click.stop="toggle"
    >
      <i class="pi text-xs" :class="loading ? 'pi-spin pi-spinner' : shown ? 'pi-eye-slash' : 'pi-eye'" />
    </button>
    <span v-if="error" class="text-xs font-sans font-normal text-red-600">couldn't load</span>
  </span>
</template>
